import type { PercentileBand } from '@projections/engine';
import { useMemo } from 'react';
import { formatCompactCurrency, formatCurrency } from '../design/format';
import { EChart } from './EChart';
import { type ProjectionTokens, resolveTokens } from './theme';

/**
 * FAN CHART — layered percentile area bands per simulated year (PLAN.md M7 "ECHARTS THEME").
 *
 * p5–p95 lightest accent tint → p25–p75 medium tint → median solid --accent. A crosshair tooltip
 * shows ALL percentiles for the hovered year as ฿. Bands are drawn as stacked area pairs (lower
 * bound transparent, upper bound tinted) so the fill sits between adjacent percentiles.
 *
 * Consumes the engine's `PercentileBand[]` (`{ p, perYear[] }`) straight from a `ResultSet.bands`.
 * Builds the ECharts option from the shared theme's colors (resolved tokens) — no hard-coded color.
 */
export interface FanChartProps {
  /** Percentile bands from `ResultSet.bands` (expected 5/25/50/75/95, ascending by p). */
  bands: readonly PercentileBand[];
  /** First simulated calendar year (for the x-axis labels = startYear + i). */
  startYear: number;
  height?: number;
}

export function FanChart({ bands, startYear, height = 380 }: FanChartProps): React.ReactElement {
  const option = useMemo(() => buildFanOption(bands, startYear), [bands, startYear]);
  return (
    <EChart option={option} height={height} aria-label="Projected wealth percentile fan chart" />
  );
}

interface FanBandPair {
  /** Lower-bound percentile. */
  readonly lo: PercentileBand;
  /** Upper-bound percentile. */
  readonly hi: PercentileBand;
  /** The fill colour for the area between `lo` and `hi`. */
  readonly fill: string;
}

/** Label a percentile in [0,1] as `P5` / `P50` / `P95` for tooltip rows + legend. */
function percentileLabel(p: number): string {
  return `P${Math.round(p * 100)}`;
}

/**
 * Choose the band fill tints by distance from the median: the outermost pair is the faintest
 * accent, the inner pair a stronger accent. Symmetric pairs share a tint so the fan reads as
 * nested ribbons narrowing toward the median.
 */
function fanFills(pairCount: number, accentFaint: string, accentWeak: string): string[] {
  if (pairCount <= 1) return [accentWeak];
  // Outer → inner: faint outermost, weak innermost. Two-pair case (5/25/50/75/95) → [faint, weak].
  const fills: string[] = [];
  for (let i = 0; i < pairCount; i++) {
    fills.push(i === pairCount - 1 ? accentWeak : accentFaint);
  }
  return fills;
}

/**
 * Build the ECharts option for the fan chart. PURE — exported for unit testing the option shape.
 *
 * For ascending bands [p05,p25,p50,p75,p95]: pair the bands symmetrically around the median
 * (p05↔p95, p25↔p75), drawing each pair as a transparent base line at the lower bound plus a tinted
 * `areaStyle` series whose value is the gap to the upper bound (so the fill sits BETWEEN the two
 * bounds via `stack`). The median (the central band) is a solid `--accent` line on top. The x-axis
 * is `startYear + i`; the value axis is formatted compactly; a crosshair `tooltip` lists every
 * percentile for the hovered year as full ฿.
 */
export function buildFanOption(
  bands: readonly PercentileBand[],
  startYear: number,
  tokens: ProjectionTokens = resolveTokens(),
): Record<string, unknown> {
  const sorted = [...bands].sort((a, b) => a.p - b.p);
  const horizon = sorted.reduce((m, b) => Math.max(m, b.perYear.length), 0);
  const years = Array.from({ length: horizon }, (_, i) => String(startYear + i));

  // No bands → a valid empty option (the scaffold/empty-result case never throws).
  if (sorted.length === 0) {
    return {
      animation: true,
      xAxis: { type: 'category', data: years },
      yAxis: { type: 'value' },
      series: [] as unknown[],
    };
  }

  // The central band (closest to the median) is drawn as a solid accent line; outer bands are
  // paired symmetrically into ribbons.
  const median = sorted.reduce<PercentileBand>(
    (best, b) => (Math.abs(b.p - 0.5) < Math.abs(best.p - 0.5) ? b : best),
    sorted[0] as PercentileBand,
  );

  // Pair ascending bands from the outside in, skipping the central/median band.
  const outer = sorted.filter((b) => b !== median);
  const pairs: FanBandPair[] = [];
  for (let i = 0, j = outer.length - 1; i < j; i++, j--) {
    const lo = outer[i] as PercentileBand;
    const hi = outer[j] as PercentileBand;
    pairs.push({ lo, hi, fill: '' });
  }
  const fills = fanFills(pairs.length, tokens.accentFaint, tokens.accentWeak);
  const filledPairs: FanBandPair[] = pairs.map((pair, i) => ({
    ...pair,
    fill: fills[i] ?? tokens.accentFaint,
  }));

  const series: Record<string, unknown>[] = [];

  // Each pair → a transparent base (the lower bound) + a tinted band (gap to the upper bound),
  // stacked so the area renders between the two bounds. Distinct stack id per pair.
  filledPairs.forEach((pair, idx) => {
    const stackId = `band-${idx}`;
    series.push({
      name: `${percentileLabel(pair.lo.p)}-base`,
      type: 'line',
      stack: stackId,
      data: pair.lo.perYear,
      lineStyle: { width: 0, opacity: 0 },
      showSymbol: false,
      silent: true,
      // Lower bound carries no fill; it only lifts the band above it to the right baseline.
      areaStyle: { opacity: 0 },
      z: 1,
      tooltip: { show: false },
    });
    series.push({
      name: `${percentileLabel(pair.lo.p)}–${percentileLabel(pair.hi.p)}`,
      type: 'line',
      stack: stackId,
      data: pair.hi.perYear.map((v, y) => v - (pair.lo.perYear[y] ?? 0)),
      lineStyle: { width: 0, opacity: 0 },
      showSymbol: false,
      silent: true,
      areaStyle: { color: pair.fill, opacity: 1 },
      z: 1 + idx,
      tooltip: { show: false },
    });
  });

  // Median as a solid accent line ON TOP.
  series.push({
    name: percentileLabel(median.p),
    type: 'line',
    data: median.perYear,
    lineStyle: { width: 2, color: tokens.accent },
    itemStyle: { color: tokens.accent },
    showSymbol: false,
    z: 10,
    tooltip: { show: false },
  });

  // A single silent carrier series holds the crosshair tooltip so the formatter can list every
  // percentile for the hovered year without the stacked base/gap series confusing it.
  series.push({
    name: '__fan_axis__',
    type: 'line',
    data: median.perYear,
    lineStyle: { width: 0, opacity: 0 },
    showSymbol: false,
    silent: true,
    z: 0,
  });

  return {
    animation: true,
    animationDuration: 600,
    grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      formatter: (params: unknown) => {
        const arr = Array.isArray(params) ? params : [params];
        const first = arr[0] as { axisValue?: string } | undefined;
        const year = first?.axisValue ?? '';
        const yi = years.indexOf(year);
        if (yi < 0) return '';
        const rows = sorted
          .slice()
          .reverse()
          .map((b) => {
            const val = b.perYear[yi] ?? 0;
            const strong = b === median ? ' style="font-weight:600"' : '';
            return `<div${strong}>${percentileLabel(b.p)}&nbsp;&nbsp;${formatCurrency(val)}</div>`;
          })
          .join('');
        return `<div style="font-weight:600;margin-bottom:4px">${year}</div>${rows}`;
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: years,
    },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: (v: number) => formatCompactCurrency(v) },
    },
    series,
  };
}
